/**
 * Feedback API service module.
 * Provides the typed submission function for POST /feedback.
 *
 * Implements US-008 (T1). Cloned structure from services/children.ts: same
 * docstring conventions, same shared axios instance, one exported async function.
 * Reuses the shared axios instance from lib/api.ts — its request interceptor
 * auto-attaches Authorization: Bearer {idToken} and its response interceptor
 * normalises errors to AuthError {code, message} (TO-LL-002).
 *
 * TO-LL-044 enforcement: the payload sent here is exactly the FeedbackPayload
 * shape (category/message/replyToMe/context), matching IC-001's camelCase body.
 *
 * TO-LL-036 enforcement: NO try/catch anywhere in this module. Errors MUST
 * propagate out of submitFeedback so the React Query mutation (T2) can surface
 * them to ParentFeedbackSheet (T4). A swallowed error here would present a
 * failed submission as a silent success to the parent.
 *
 * Two things this module deliberately does NOT do, so a future reader does not
 * add them back:
 *  - No client-side retry. A retried POST on a request the server may already
 *    have committed produces a duplicate feedback record and a duplicate email
 *    (IC-001 explicitly records there is no idempotency key, no transactional
 *    dedupe). The in-flight disabled button (BR-008.1) is the whole defence.
 *  - No error mapping or swallowing here. The sheet (T4) maps AuthError to a
 *    fixed client-owned copy; this module never touches error content.
 */

import { api } from '@/lib/api';
import type { FeedbackPayload, FeedbackSubmitResponse } from '@/types/feedback';

/**
 * Submit a parent's feedback.
 *
 * POST /feedback
 * Body: { category, message, replyToMe, context } — exact IC-001 shape.
 * Response 201: { feedbackId, message } — the client never renders either field.
 *
 * Errors propagate (not caught) — React Query mutation surfaces them to
 * ParentFeedbackSheet, which maps them to a fixed, non-disclosive copy.
 *
 * @param payload - { category, message, replyToMe, context }
 * @returns FeedbackSubmitResponse ({ feedbackId, message })
 */
export async function submitFeedback(payload: FeedbackPayload): Promise<FeedbackSubmitResponse> {
  const response = await api.post<FeedbackSubmitResponse>('/feedback', payload);
  return response.data;
}
